import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  Key,
  ArrowBack,
  CheckCircle,
  Security,
  Shield,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import UserService from '../../services/userService';
import useRoleColors from '../../hooks/useRoleColors';

const ChangePassword = () => {
  const theme = useTheme();
  const {
    accent,
    accentLight,
    accentDark,
    accentGradient,
    accentGradientHover,
    softBg,
    softBgHover,
    softBgStrong,
    outline,
    contrast,
  } = useRoleColors();
  const navigate = useNavigate();
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Handle password input changes
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    if (error) setError('');
    if (success) setSuccess('');
  };

  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)) {
      errors.newPassword = 'Password must contain at least one uppercase letter, lowercase letter, and number';
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Password confirmation is required';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (passwordData.currentPassword === passwordData.newPassword) {
      errors.newPassword = 'New password cannot be the same as current password';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Change password
  const changePassword = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const result = await UserService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      if (result.success) {
        setSuccess(result.message);
        toast.success(result.message);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordErrors({});
        
        // Redirect after 2 seconds
        setTimeout(() => {
          navigate('/profile');
        }, 2000);
      } else {
        setError(result.message || 'Password could not be changed');
        toast.error(result.message || 'Password could not be changed');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Server error occurred');
      toast.error('Server error occurred');
    } finally {
      setSaving(false);
    }
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    changePassword();
  };

  // Reset form
  const handleReset = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordErrors({});
    setError('');
    setSuccess('');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>

        {/* Password Change Card */}
        <Card elevation={8} sx={{ 
          borderRadius: 4,
          overflow: 'hidden',
          mb: 4
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: accentGradient,
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Box sx={{ 
                  width: 64, 
                  height: 64,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  <Key sx={{ fontSize: 32 }} />
                </Box>
              </Grid>
              <Grid item xs>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Change Password
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Create a strong password to protect your account
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={() => navigate('/profile')}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  Back to Profile
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Form Content */}
          <CardContent sx={{ p: 4 }}>
            <Box component="form" onSubmit={handleSubmit}>
              
              {/* Password Fields */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  mb: 3, 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: 600,
                  color: 'text.primary'
                }}>
                  <Security sx={{ mr: 1.5, color: accent }} />
                  Password Information
                </Typography>

                <Grid container spacing={3}>
                  {/* Current Password */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                      variant="outlined"
                      error={!!passwordErrors.currentPassword}
                      helperText={passwordErrors.currentPassword}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color={passwordData.currentPassword ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                              edge="end"
                            >
                              {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: accent,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: accent,
                          }
                        }
                      }}
                    />
                  </Grid>

                  {/* New Password */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      variant="outlined"
                      error={!!passwordErrors.newPassword}
                      helperText={passwordErrors.newPassword || 'Min. 8 characters'}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Key color={passwordData.newPassword ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                              edge="end"
                            >
                              {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: accent,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: accent,
                          }
                        }
                      }}
                    />
                  </Grid>

                  {/* Confirm New Password */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      variant="outlined"
                      error={!!passwordErrors.confirmPassword}
                      helperText={passwordErrors.confirmPassword}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Key color={passwordData.confirmPassword ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                              edge="end"
                            >
                              {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: accent,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: accent,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Password Requirements */}
              <Paper sx={{ 
                p: 2.5, 
                mb: 4, 
                backgroundColor: softBg,
                border: `1px solid ${outline}`,
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Shield sx={{ color: accent, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: accent }}>
                      Password Requirements:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        At least 8 characters long
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Contains at least one uppercase letter
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Contains at least one lowercase letter
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Contains at least one number
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Different from current password
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Error/Success Messages */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={saving}
                  startIcon={<ArrowBack />}
                  sx={{
                    borderColor: accent,
                    color: accent,
                    '&:hover': {
                      borderColor: accentLight,
                      backgroundColor: softBg
                    }
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                  sx={{
                    minWidth: 180,
                    background: accentGradient,
                    boxShadow: `0 4px 15px ${accent}40`,
                    '&:hover': {
                      background: accentGradientHover,
                      boxShadow: `0 6px 20px ${accent}66`,
                    }
                  }}
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ChangePassword;